import {
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  Stack,
  type SxProps,
  styled,
  useTheme,
} from '@mui/material';
import { useFileUploadContext } from 'src/provider/FileUploadContext';

import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useMemo, useRef, useState } from 'react';
import { CircularProgressWithLabel } from 'src/components/CircularProgressWithLabel';
import FileThumbnail from 'src/components/file-thumbnail';
import { Iconify } from 'src/components/iconify';
import { fData } from 'src/utils/formatNumber';

const mocks = [
  {
    id: 1.2,
    file: new File(
      ['124'],
      'test wer ewr wer wer wer wer wer wer ewr wer ewr erewr wer wer ewr wer.svg',
    ),
    progress: 10,
    speed: 90,
  },
  { id: 1, file: new File(['124'], 'test.svg'), progress: 45 },
  { id: 2, file: new File(['124'], 'test.pdf'), progress: 45, speed: 100 },
  { id: 3, file: new File(['124'], 'test.mp3'), isCompleted: true },
  {
    id: 42,
    file: new File(['124'], 'test.pdf'),
    progress: 0,
    speed: undefined,
  },
];

type Props = {
  sx?: SxProps;
};

export const FileUploadProgress = ({ sx }: Props) => {
  const { control } = useFileUploadContext();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const items = control.files;
  const totalItems = items.length;

  const totalSpeed = useMemo(
    () =>
      items.reduce((acc, item) => {
        if (item.speed) {
          return acc + item.speed;
        }
        return acc;
      }, 0),
    [items],
  );

  const totalItemsInProgress = useMemo(
    () =>
      items.reduce(
        (acc, item) => acc + (item.isCompleted ? 100 : item.progress || 0),
        0,
      ),
    [items],
  );

  const avgSpeed = totalSpeed / totalItems;
  const totalProgress = totalItemsInProgress / totalItems;

  const isMultiple = totalItems > 1;

  if (!totalItems) return null;

  return (
    <>
      <Stack
        gap={2}
        direction="row"
        alignItems="center"
        px={2}
        py={2}
        sx={{
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.default,
          maxWidth: '100%',
          overflow: 'hidden',
          ...sx,
        }}
        ref={anchorRef}
      >
        <CircularProgressWithLabel value={totalProgress} />

        <Stack
          flexGrow={1}
          direction="column"
          sx={{
            flexShrink: 1,
            overflow: 'hidden',
            maxWidth: '100%',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            flexGrow={1}
            sx={{
              maxWidth: '100%',
            }}
          >
            {isMultiple ? (
              <Typography variant="subtitle2">
                {`uploading ${totalItems} files`}
              </Typography>
            ) : (
              <FileName name={items[0].file.name} />
            )}
          </Stack>
          <Typography variant="caption" sx={{ flexGrow: 1 }}>
            {avgSpeed ? `${fData(avgSpeed)}/s` : ''}
          </Typography>
        </Stack>
        {isMultiple && (
          <IconButton onClick={() => setOpen(true)}>
            <Iconify icon="tabler:chevron-up" />
          </IconButton>
        )}
      </Stack>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: anchorRef.current?.clientWidth,
          },
        }}
      >
        {items.map((item) => (
          <ListItem key={item.id}>
            <ListItemAvatar>
              <FileThumbnail file={item.file} />
            </ListItemAvatar>
            <ListItemText
              primary={item.file.name}
              primaryTypographyProps={{
                variant: 'subtitle2',
                sx: {
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                },
              }}
              secondary={item.speed ? `${fData(item.speed)}/s` : undefined}
            />
            {item.isCompleted ? (
              <Iconify icon="tabler:check" />
            ) : (
              <CircularProgress
                value={item.progress}
                sx={{
                  flexShrink: 0,
                }}
                variant={
                  !item.isCompleted && item.progress
                    ? 'determinate'
                    : 'indeterminate'
                }
                size={24}
              />
            )}
          </ListItem>
        ))}
      </Menu>
    </>
  );
};

const FileName = ({ name }: { name: string }) => {
  const [extension, ...rest] = name.split('.').reverse();
  const fileName = rest.join('.');

  return (
    <Typography
      component="span"
      variant="subtitle2"
      noWrap
      sx={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        flexGrow: 1,
        display: 'flex',
      }}
    >
      <StyledEllipsisSpan>{fileName}</StyledEllipsisSpan>
      <StyledExtensionName>.{extension}</StyledExtensionName>
    </Typography>
  );
};

const StyledEllipsisSpan = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const StyledExtensionName = styled('span')({
  flexShrink: 0,
});
